// src/services/system-admin/transport-review.service.ts
import { API_BASE_URL } from "@/lib/api";
import { adminAuthService } from "@/services/system-admin/auth.service";
import type {
  VehicleListItem,
  PagedResult,
  VehicleAdminDetail,
  VehicleReview,
  VehicleAvailabilityBlock,
  GetVehiclesParams,
  ApproveVehicleRequest,
  RequestChangesVehicleRequest,
  RejectVehicleRequest,
  SuspendVehicleRequest,
  RestoreVehicleRequest,
  VerifyDocumentRequest,
  RejectDocumentRequest,
} from "@/types/admin/transport-approve-type";

function authHeaders(): HeadersInit {
  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("adminAccessToken") || ""
      : "";
  return {
    Accept: "application/json, text/plain, */*",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

let refreshPromise: Promise<void> | null = null;

async function refreshAccessToken(): Promise<void> {
  if (typeof window === "undefined") return;
  const rt = localStorage.getItem("adminRefreshToken");
  if (!rt) throw new Error("No refresh token available");

  const data = await adminAuthService.refresh(rt);
  localStorage.setItem("adminAccessToken", data.accessToken);
  localStorage.setItem("adminRefreshToken", data.refreshToken);
}

/** fetch wrapper that transparently refreshes the token once on 401 and retries */
async function authFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  let res = await fetch(url, {
    ...options,
    headers: { ...options.headers, ...authHeaders() },
  });

  if (res.status === 401) {
    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;

      res = await fetch(url, {
        ...options,
        headers: { ...options.headers, ...authHeaders() },
      });
    } catch {
      if (typeof window !== "undefined") {
        localStorage.removeItem("adminAccessToken");
        localStorage.removeItem("adminRefreshToken");
        window.location.href = "/admin/system-admin/login";
      }
    }
  }

  return res;
}

async function handle<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    let message = fallback;
    try {
      const errData = await res.json();
      message = errData?.title || errData?.message || errData?.error || fallback;
    } catch {
      // response wasn't JSON — keep fallback
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

async function handleVoid(res: Response, fallback: string): Promise<void> {
  if (!res.ok) {
    let message = fallback;
    try {
      const errData = await res.json();
      message = errData?.title || errData?.message || errData?.error || fallback;
    } catch {
      // response wasn't JSON — keep fallback
    }
    throw new Error(message);
  }
  // No body expected (e.g. 204 No Content) — nothing to parse.
}

export const transportReviewService = {
  /** GET /api/admin/transport/vehicles */
  async getVehicles(
    params: GetVehiclesParams,
  ): Promise<PagedResult<VehicleListItem>> {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    if (params.search) query.set("search", params.search);
    if (params.partnerId) query.set("partnerId", String(params.partnerId));
    if (params.submittedFrom) query.set("submittedFrom", params.submittedFrom);
    if (params.submittedTo) query.set("submittedTo", params.submittedTo);
    query.set("page", String(params.page ?? 1));
    query.set("pageSize", String(params.pageSize ?? 20));

    const res = await authFetch(
      `${API_BASE_URL}/api/admin/transport/vehicles?${query.toString()}`,
      { method: "GET" },
    );
    return handle<PagedResult<VehicleListItem>>(res, "Failed to load vehicles.");
  },

  /** GET /api/admin/transport/vehicles/{vehicleUid}?status=ALL */
  async getVehicleById(vehicleUid: string): Promise<VehicleAdminDetail> {
    const res = await authFetch(
      `${API_BASE_URL}/api/admin/transport/vehicles/${vehicleUid}?status=ALL`,
      { method: "GET" },
    );
    return handle<VehicleAdminDetail>(res, "Failed to load vehicle details.");
  },

  /** GET /api/admin/transport/vehicles/{vehicleUid}/reviews */
  async getVehicleReviews(vehicleUid: string): Promise<VehicleReview[]> {
    const res = await authFetch(
      `${API_BASE_URL}/api/admin/transport/vehicles/${vehicleUid}/reviews`,
      { method: "GET" },
    );
    return handle<VehicleReview[]>(res, "Failed to load review history.");
  },

  /** GET /api/admin/transport/vehicles/{vehicleUid}/availability */
  async getVehicleAvailability(
    vehicleUid: string,
    fromDate?: string,
    toDate?: string,
  ): Promise<VehicleAvailabilityBlock[]> {
    const query = new URLSearchParams();
    if (fromDate) query.set("fromDate", fromDate);
    if (toDate) query.set("toDate", toDate);
    const qs = query.toString();
    const res = await authFetch(
      `${API_BASE_URL}/api/admin/transport/vehicles/${vehicleUid}/availability${
        qs ? `?${qs}` : ""
      }`,
      { method: "GET" },
    );
    return handle<VehicleAvailabilityBlock[]>(
      res,
      "Failed to load availability.",
    );
  },

  /** PATCH /api/admin/transport/vehicles/{vehicleUid}/approve */
  async approveVehicle(
    vehicleUid: string,
    payload: ApproveVehicleRequest,
  ): Promise<void> {
    const res = await authFetch(
      `${API_BASE_URL}/api/admin/transport/vehicles/${vehicleUid}/approve`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    return handleVoid(res, "Failed to approve vehicle.");
  },

  /** PATCH /api/admin/transport/vehicles/{vehicleUid}/request-changes */
  async requestChanges(
    vehicleUid: string,
    payload: RequestChangesVehicleRequest,
  ): Promise<void> {
    const res = await authFetch(
      `${API_BASE_URL}/api/admin/transport/vehicles/${vehicleUid}/request-changes`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    return handleVoid(res, "Failed to request changes.");
  },

  /** PATCH /api/admin/transport/vehicles/{vehicleUid}/reject */
 async rejectVehicle(
    vehicleUid: string,
    payload: RejectVehicleRequest,
  ): Promise<void> {
    const res = await authFetch(
      `${API_BASE_URL}/api/admin/transport/vehicles/${vehicleUid}/reject`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    return handleVoid(res, "Failed to reject vehicle.");
  },

  /** PATCH /api/admin/transport/vehicles/{vehicleUid}/suspend */
async suspendVehicle(
    vehicleUid: string,
    payload: SuspendVehicleRequest,
  ): Promise<void> {
    const res = await authFetch(
      `${API_BASE_URL}/api/admin/transport/vehicles/${vehicleUid}/suspend`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    return handleVoid(res, "Failed to suspend vehicle.");
  },

  /** PATCH /api/admin/transport/vehicles/{vehicleUid}/restore */
async restoreVehicle(
    vehicleUid: string,
    payload: RestoreVehicleRequest,
  ): Promise<void> {
    const res = await authFetch(
      `${API_BASE_URL}/api/admin/transport/vehicles/${vehicleUid}/restore`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    return handleVoid(res, "Failed to restore vehicle.");
  },

  /** PATCH /api/admin/transport/vehicle-documents/{documentUid}/verify */
  async verifyDocument(
    documentUid: string,
    payload: VerifyDocumentRequest,
  ): Promise<void> {
    const res = await authFetch(
      `${API_BASE_URL}/api/admin/transport/vehicle-documents/${documentUid}/verify`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
await handleVoid(res, "Failed to verify document.");
  },

  /** PATCH /api/admin/transport/vehicle-documents/{documentUid}/reject */
  async rejectDocument(
    documentUid: string,
    payload: RejectDocumentRequest,
  ): Promise<void> {
    const res = await authFetch(
      `${API_BASE_URL}/api/admin/transport/vehicle-documents/${documentUid}/reject`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
  await handleVoid(res, "Failed to reject document.");
  },
};